import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useTheme } from "@/context/ThemeContext";
import TablePager from "@/components/TablePager";
import { DatePicker } from "@/components/ui/date-picker";
import { toast } from "sonner";
import { Receipt, FileDown, Eye, Search, Download, Layers, ChevronRight, ChevronDown, AlertCircle } from "lucide-react";
import { formatDate } from "@/lib/utils";

const PAGE_SIZE = 25;
const TOTAL_COLS = 9; // checkbox, Invoice #, Client Ref, Jobs, Invoice Date, Due Date, Amount, Status, Actions

// Group a (merged) invoice's line items by their source job so the merged view
// can show the individual jobs consolidated into it — each labelled with the
// customer's own Client Ref (never DRIVN's internal carrier_ref).
function groupLinesByJob(inv) {
  const groups = {};
  (inv.line_items || []).forEach((l) => {
    const key = l.job_id || l.job_ref || "(custom)";
    if (!groups[key]) groups[key] = { job_id: l.job_id, lines: [], subtotal: 0 };
    groups[key].lines.push(l);
    groups[key].subtotal += Number(l.qty || 0) * Number(l.rate || 0);
  });
  return Object.values(groups);
}

// Mirrors Invoicing.jsx's INVOICE_STATUS_OPTIONS colours, read-only here —
// clients can view their invoice status but not change it. Draft -> Pending
// Payment (automatic, the day after admin sends it) -> Overdue (automatic,
// once the due date passes) -> Paid (set by admin once payment is received).
const STATUS_META = {
  draft:       { label: "Draft",            color: "#a5b4fc", cl: "#4f46e5" },
  pending:     { label: "Pending Payment",  color: "#facc15", cl: "#b45309" },
  overdue:     { label: "Overdue",          color: "#ef4444", cl: "#dc2626" },
  paid:        { label: "Paid",             color: "#22c55e", cl: "#15803d" },
};

const fmt = (n) => `$${Number(n || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function StatusPill({ status }) {
  const { theme } = useTheme();
  const meta = STATUS_META[status] || STATUS_META.draft;
  const c = theme === "light" ? (meta.cl || meta.color) : meta.color;
  return (
    <span className="text-xs font-semibold rounded-md px-2 py-1.5 inline-block" style={{ background: c + "1f", color: c }}>
      {meta.label}
    </span>
  );
}

async function fetchInvoicePdfBlob(id) {
  const res = await api.get(`/invoices/${id}/pdf`, { responseType: "blob" });
  return new Blob([res.data], { type: "application/pdf" });
}

// Client-facing Invoicing tab — a read-only, self-scoped mirror of the admin
// Invoicing page: lets a customer see every invoice raised against them,
// preview/download the PDF, and pull a Statement of Account CSV for their
// own reconciliation.
export default function ClientInvoicing() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("normal");      // "normal" | "merged"
  const [filter, setFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [busyId, setBusyId] = useState(null);
  const [soaBusy, setSoaBusy] = useState(false);
  const [selected, setSelected] = useState({});  // invoice id → bool (bulk selection, scoped to what's currently visible)
  const [bulkBusy, setBulkBusy] = useState(false);
  const [expanded, setExpanded] = useState({});  // merged invoice id → show constituent jobs

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/invoices/mine");
      setInvoices(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Normal = standalone singles. Merged = consolidated across several jobs.
  const normal = useMemo(() => invoices.filter((i) => i.type === "single" && !i.merged_into), [invoices]);
  const merged = useMemo(() => invoices.filter((i) => i.type === "merged"), [invoices]);
  const source = tab === "merged" ? merged : normal;
  const normalOverdue = useMemo(() => normal.filter((i) => i.invoice_status === "overdue").length, [normal]);
  const mergedOverdue = useMemo(() => merged.filter((i) => i.invoice_status === "overdue").length, [merged]);
  const totalOverdue = normalOverdue + mergedOverdue;
  const searching = filter.trim().length > 0 || !!dateFrom || !!dateTo;

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    // A Client Ref search, or an invoice-date range, must surface a match
    // whether the invoice is Normal or Merged — so once either filter is set,
    // matching runs over every invoice, not just the active tab's subset.
    const base = q || dateFrom || dateTo ? invoices : source;
    return base.filter((i) => {
      if (q) {
        const hit = [i.invoice_number, ...(i.client_job_refs || [])]
          .filter(Boolean).map((x) => String(x).toLowerCase()).some((x) => x.includes(q));
        if (!hit) return false;
      }
      const invDate = (i.invoice_date || "").slice(0, 10);
      if (dateFrom && invDate < dateFrom) return false;
      if (dateTo && invDate > dateTo) return false;
      return true;
    });
  }, [invoices, source, filter, dateFrom, dateTo]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = useMemo(() => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [filtered, page]);
  useEffect(() => { setPage(1); }, [filter, dateFrom, dateTo, tab]);
  useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);
  // Clear the bulk selection whenever the visible list changes, so an invoice
  // that's been filtered/tabbed out of view can never be silently included in
  // a bulk download (mirrors the same safeguard on the Job History page).
  useEffect(() => { setSelected({}); }, [filter, dateFrom, dateTo, tab]);

  // Bulk selection — scoped to whatever's currently visible (`filtered`), so it
  // stays correct whether the active tab or a cross-tab search produced the rows.
  // Read-only account: there is no bulk delete here at all, only Download.
  const selectedList = useMemo(() => filtered.filter((i) => selected[i.id]), [filtered, selected]);
  const selectedTotal = selectedList.reduce((a, i) => a + Number(i.total || 0), 0);
  const allOnPageSelected = pageItems.length > 0 && pageItems.every((i) => selected[i.id]);
  const toggleSelectAllOnPage = () => {
    const checked = !allOnPageSelected;
    setSelected((s) => {
      const next = { ...s };
      pageItems.forEach((i) => { next[i.id] = checked; });
      return next;
    });
  };

  const previewPdf = async (inv) => {
    setBusyId(inv.id);
    // Open the tab synchronously, inside the click handler, then navigate it
    // once the PDF is fetched — opening it only after the await is what
    // Safari/iOS treat as an untrusted popup and silently block or strand on
    // about:blank. Not passing noopener/noreferrer here since we need the
    // window reference back to route the blob into it; safe because the tab
    // only ever gets pointed at our own same-origin blob URL.
    const w = window.open("", "_blank");
    try {
      const blob = await fetchInvoicePdfBlob(inv.id);
      if (!w) { toast.error("Please allow pop-ups to preview invoices"); return; }
      w.location.href = URL.createObjectURL(blob);
    } catch (e) {
      if (w) w.close();
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    finally { setBusyId(null); }
  };

  const downloadPdf = async (inv) => {
    setBusyId(inv.id);
    try {
      const blob = await fetchInvoicePdfBlob(inv.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${inv.invoice_number || "invoice"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  // Bulk download — reuses the same single-invoice fetch+save as the per-row
  // Download button, just looped with a short stagger so the browser doesn't
  // treat a burst of simultaneous downloads as a popup flood and block them.
  // There is no bulk delete on this page — clients can only ever view/download.
  const bulkDownloadSelected = async () => {
    if (selectedList.length === 0) return;
    setBulkBusy(true);
    try {
      for (const inv of selectedList) {
        await downloadPdf(inv);
        await new Promise((r) => setTimeout(r, 300));
      }
      toast.success(`Downloaded ${selectedList.length} invoice${selectedList.length !== 1 ? "s" : ""}`);
    } finally {
      setBulkBusy(false);
    }
  };

  const downloadSoa = async () => {
    setSoaBusy(true);
    try {
      const res = await api.get("/invoices/statement-of-account", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `SOA-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSoaBusy(false); }
  };

  return (
    <div data-testid="client-invoicing-page">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <div>
          <h1 className="brand-title text-2xl sm:text-3xl flex items-center gap-3">
            <Receipt className="text-pink-400" size={26} /> Invoicing
            {totalOverdue > 0 && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-red-500/15 text-red-400" data-testid="client-overdue-badge">
                <AlertCircle size={13} /> {totalOverdue} Overdue
              </span>
            )}
          </h1>
          <p className="text-white/60 text-sm mt-1">
            Every invoice raised against your account — preview or download the PDF, or export a Statement of Account.
          </p>
        </div>
        <button
          onClick={downloadSoa}
          disabled={soaBusy}
          className="px-4 py-2.5 rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
          style={{ background: "rgba(25,230,200,0.1)", color: "#19e6c8" }}
          data-testid="download-soa"
        >
          <FileDown size={15} /> {soaBusy ? "Preparing…" : "Download SOA (CSV)"}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative w-full max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
          <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search invoice # / client ref" className="df-input-rect" style={{ paddingLeft: "2.5rem" }} data-testid="client-invoice-search" />
        </div>
        <span className="text-white/80 font-semibold text-sm">From:</span>
        <DatePicker value={dateFrom} onChange={setDateFrom} max={dateTo || undefined} className="!py-2 !text-sm" data-testid="client-invoice-date-from" />
        <span className="text-white/80 font-semibold text-sm">To:</span>
        <DatePicker value={dateTo} onChange={setDateTo} min={dateFrom || undefined} className="!py-2 !text-sm" data-testid="client-invoice-date-to" />
        {(dateFrom || dateTo) && (
          <button onClick={() => { setDateFrom(""); setDateTo(""); }} className="text-white/60 hover:text-white text-sm underline">Clear</button>
        )}
      </div>

      {/* Section tabs */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {[
          { key: "normal", label: `Normal (${normal.length})`, icon: Receipt, overdue: normalOverdue },
          { key: "merged", label: `Merged (${merged.length})`, icon: Layers, overdue: mergedOverdue },
        ].map((t) => {
          const on = tab === t.key && !searching;
          const Icon = t.icon;
          return (
            <button key={t.key} type="button" onClick={() => setTab(t.key)}
              className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-colors flex items-center gap-2 ${
                on ? "bg-pink-400/20 text-pink-300" : "bg-white/5 text-white/50 hover:bg-white/10"}`}
              data-testid={`client-invoice-tab-${t.key}`}>
              <Icon size={14} /> {t.label}
              {t.overdue > 0 && (
                <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-red-500 text-white" data-testid={`client-overdue-badge-${t.key}`}>
                  {t.overdue}
                </span>
              )}
            </button>
          );
        })}
        {searching && (
          <span className="text-xs text-white/40">
            Showing matches from Normal + Merged
            {filter.trim() ? ` for "${filter.trim()}"` : ""}
            {(dateFrom || dateTo) ? ` · ${dateFrom || "…"} to ${dateTo || "…"}` : ""}
          </span>
        )}
      </div>

      {/* Bulk action bar — appears once one or more invoices are selected.
          Clients can only ever preview/download; there is no delete or merge
          affordance anywhere on this page. */}
      {selectedList.length > 0 && (
        <div className="rounded-xl border border-pink-400/30 bg-pink-400/10 px-4 py-3 mb-4 flex items-center justify-between flex-wrap gap-3" data-testid="client-bulk-actions-bar">
          <div className="text-sm text-white/80">
            <span className="font-bold">{selectedList.length}</span> invoice{selectedList.length !== 1 ? "s" : ""} selected · <span className="font-mono font-bold">{fmt(selectedTotal)}</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setSelected({})} className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-white/80 text-xs font-semibold">Clear</button>
            <button onClick={bulkDownloadSelected} disabled={bulkBusy}
              className="px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
              style={{ background: "rgba(25,230,200,0.15)", color: "#19e6c8" }} data-testid="client-bulk-download-btn">
              <Download size={15} /> {bulkBusy ? "Working…" : "Download"}
            </button>
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.04] text-xs uppercase tracking-wider text-white/60">
              <tr>
                <th className="px-3 py-3 w-8">
                  <input
                    type="checkbox"
                    checked={allOnPageSelected}
                    onChange={toggleSelectAllOnPage}
                    className="accent-pink-500 w-4 h-4 cursor-pointer"
                    aria-label="Select all on page"
                    data-testid="client-invoice-select-all"
                  />
                </th>
                <th className="px-3 py-3 text-left whitespace-nowrap">Invoice #</th>
                <th className="px-3 py-3 text-left whitespace-nowrap">Client Ref</th>
                <th className="px-3 py-3 text-left whitespace-nowrap">Jobs</th>
                <th className="px-3 py-3 text-left whitespace-nowrap">Invoice Date</th>
                <th className="px-3 py-3 text-left whitespace-nowrap">Due Date</th>
                <th className="px-3 py-3 text-right whitespace-nowrap">Amount</th>
                <th className="px-3 py-3 text-left whitespace-nowrap">Status</th>
                <th className="px-3 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={TOTAL_COLS} className="px-3 py-6 text-center text-white/40">Loading…</td></tr>}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={TOTAL_COLS} className="px-3 py-6 text-center text-white/40">
                  {searching ? "No invoices match your search." : tab === "merged" ? "No merged invoices yet." : "No invoices yet."}
                </td></tr>
              )}
              {pageItems.map((inv) => {
                const jobGroups = inv.type === "merged" ? groupLinesByJob(inv) : [];
                const isOpen = !!expanded[inv.id];
                return (
                  <React.Fragment key={inv.id}>
                    <tr className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`client-invoice-row-${inv.id}`}>
                      <td className="px-3 py-3">
                        <input type="checkbox" checked={!!selected[inv.id]} onChange={(e) => setSelected((s) => ({ ...s, [inv.id]: e.target.checked }))}
                          className="accent-pink-500 w-4 h-4 cursor-pointer" aria-label={`Select ${inv.invoice_number}`} data-testid={`client-invoice-sel-${inv.id}`} />
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <div className="text-white font-semibold font-mono">{inv.invoice_number}</div>
                        {(inv.job_refs || []).length > 0 && (
                          <div className="text-[10px] font-mono text-white/40 mt-0.5 truncate max-w-[160px]" title={(inv.job_refs || []).join(", ")}>
                            {(inv.job_refs || []).join(", ")}
                          </div>
                        )}
                        {inv.is_merged && (
                          <div className="text-[10px] text-pink-300 mt-0.5">covers {(inv.client_job_refs || []).length} job(s)</div>
                        )}
                      </td>
                      <td className="px-3 py-3 text-white/85 max-w-[220px] truncate" title={(inv.client_job_refs || []).join(", ")}>
                        {(inv.client_job_refs || []).join(", ") || "—"}
                      </td>
                      {inv.type === "merged" ? (
                        <td className="px-3 py-3">
                          <button onClick={() => setExpanded((x) => ({ ...x, [inv.id]: !x[inv.id] }))}
                            className="inline-flex items-center gap-1 text-xs text-white/70 hover:text-white"
                            data-testid={`client-merged-expand-${inv.id}`} title="Show the individual jobs in this invoice">
                            {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            <span className="font-mono">{(inv.client_job_refs || []).length} job{(inv.client_job_refs || []).length !== 1 ? "s" : ""}</span>
                          </button>
                        </td>
                      ) : (
                        <td className="px-3 py-3 text-white/30">—</td>
                      )}
                      <td className="px-3 py-3 text-white/70 whitespace-nowrap text-xs font-mono">{inv.invoice_date ? formatDate(inv.invoice_date) : "—"}</td>
                      <td className="px-3 py-3 text-white/70 whitespace-nowrap text-xs font-mono">{inv.due_date ? formatDate(inv.due_date) : "—"}</td>
                      <td className="px-3 py-3 text-right font-mono font-bold text-white whitespace-nowrap">{fmt(inv.total)}</td>
                      <td className="px-3 py-3"><StatusPill status={inv.invoice_status} /></td>
                      <td className="px-3 py-3 text-right whitespace-nowrap">
                        <div className="inline-flex items-center gap-1">
                          <button onClick={() => previewPdf(inv)} disabled={busyId === inv.id}
                            className="px-2.5 py-1.5 rounded-lg text-[11px] font-semibold text-[#19e6c8] bg-[#19e6c8]/10 hover:bg-[#19e6c8]/20 inline-flex items-center gap-1 disabled:opacity-50"
                            title="Preview invoice PDF" data-testid={`preview-${inv.id}`}>
                            <Eye size={13} /> Preview
                          </button>
                          <button onClick={() => downloadPdf(inv)} disabled={busyId === inv.id}
                            className="px-2.5 py-1.5 rounded-lg text-[11px] font-semibold text-white/70 bg-white/5 hover:bg-white/10 inline-flex items-center gap-1 disabled:opacity-50"
                            title="Download invoice PDF" data-testid={`download-${inv.id}`}>
                            <Download size={13} /> Download
                          </button>
                        </div>
                      </td>
                    </tr>
                    {inv.type === "merged" && isOpen && (
                      <tr className="bg-white/[0.015]" data-testid={`client-merged-detail-${inv.id}`}>
                        <td colSpan={TOTAL_COLS} className="px-6 py-3">
                          <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2">Individual jobs in this invoice</div>
                          <div className="space-y-2">
                            {jobGroups.map((g, gi) => (
                              <div key={g.job_id || gi} className="rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="font-mono text-xs text-pink-300">
                                    Client Ref: {inv.client_ref_by_job?.[g.job_id] || "—"}
                                  </span>
                                  <span className="font-mono text-xs text-white/70">{fmt(g.subtotal)} <span className="text-white/35">ex GST</span></span>
                                </div>
                                <table className="w-full text-[11px]">
                                  <tbody>
                                    {g.lines.map((l, i) => (
                                      <tr key={l.id || i}>
                                        <td className="py-0.5 text-white/70">{l.description}</td>
                                        <td className="py-0.5 text-right font-mono text-white/55 whitespace-nowrap">{Number(l.qty || 0)} × {fmt(l.rate)}</td>
                                        <td className="py-0.5 text-right font-mono text-white/80 whitespace-nowrap pl-3">{fmt(Number(l.qty || 0) * Number(l.rate || 0))}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ))}
                            {jobGroups.length === 0 && <div className="text-white/40 text-xs">No line items.</div>}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <TablePager page={page} totalPages={totalPages} total={filtered.length} pageSize={PAGE_SIZE} onPage={setPage} />
      </div>
    </div>
  );
}
